import { memo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';

import { deleteImage } from 'apis/auction';
import { removeImage, setAuctionFormState } from 'features/auctionFormSlice';
import useAppSelector from 'hooks/useAppSelector';
import DraggableImage from '../DraggableImage/DraggableImage';
import styles from './ImageCarousel.module.css';
import { DragDropContext, DropResult, Droppable, DroppableProvided } from 'react-beautiful-dnd';
import reorder from 'utils/reorder';

interface ImageCarouselProps {
  openImageGallery: (index: number) => void;
}

function ImageCarousel({ openImageGallery }: ImageCarouselProps) {
  const dispatch = useDispatch();
  const { id, images } = useAppSelector((state) => state.auctionForm);

  const [deletingImageIds, setDeletingImageIds] = useState<Set<string>>(new Set());

  const tryDeleteImage = (targetImageId: string) => () => {
    if (deletingImageIds.has(targetImageId)) return;

    setDeletingImageIds(new Set(deletingImageIds).add(targetImageId));

    deleteImage(id, targetImageId)
      .then(() => {
        dispatch(removeImage(targetImageId));
      })
      .catch((errMsg) => {
        if (errMsg) message.error(errMsg);
      })
      .finally(() => {
        const newDeletingImageIds = new Set(deletingImageIds);
        newDeletingImageIds.delete(targetImageId);
        setDeletingImageIds(newDeletingImageIds);
      });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const reorderedImages = reorder(images, result.source.index, result.destination.index);
    dispatch(setAuctionFormState({ images: reorderedImages }));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="images" direction="horizontal">
        {(dropProvided: DroppableProvided) => (
          <div className={styles.wrapper} {...dropProvided.droppableProps}>
            <div className={styles.container}>
              <div ref={dropProvided.innerRef} className={styles['inner-container']}>
                {images.map(({ id, url }, index) => (
                  <DraggableImage
                    key={id}
                    id={id}
                    url={url}
                    index={index}
                    isBeingDeleted={deletingImageIds.has(id)}
                    showRepresentativeTag={index === 0}
                    tryDeleteImage={tryDeleteImage(id)}
                    openImageGallery={() => {
                      openImageGallery(index);
                    }}
                  />
                ))}
                {dropProvided.placeholder}
              </div>
            </div>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default memo(ImageCarousel);
